import './DataTable.scss'
import { useId, type MouseEvent, type ReactNode } from 'react'
import { MdOutlineArrowDownward, MdOutlineArrowUpward, MdOutlineUnfoldMore } from 'react-icons/md'
import { clx } from '../clx'
import { Skeleton } from '../Skeleton'
import { useTableFrame } from '../TableFrame/context'
import { Tooltip } from '../Tooltip'
import { VisuallyHidden } from '../VisuallyHidden'

export type SortOrder = 'ASC' | 'DESC'

export interface DataTableSort {
  field: string
  order: SortOrder
}

export interface DataTableColumn<Row> {
  key: string
  header: ReactNode
  /** Plain-text name for the stacked layout's data-label, when `header` is not a string. */
  label?: string
  /** Cell content. Defaults to the `field` value as text. */
  render?: (row: Row) => ReactNode
  /** Default accessor, and the sort field (falls back to `key`). */
  field?: keyof Row & string
  /** Default: true when `field` is set. Needs `onSortChange` to be interactive. */
  isSortable?: boolean
  /**
   * Width hint as a percentage or relative length ('40%', '12rem'). Never px.
   * From `desktop` columns size to their content (ADR 0022); use this only to
   * give a long-text column room.
   */
  width?: string
  /**
   * Long text (portal TruncatedTextField): wraps, clamped to three lines from
   * `desktop`, with the full text in a tooltip.
   */
  isTruncated?: boolean
  align?: 'start' | 'end'
  /**
   * What the column holds, for alignment and wrapping (ADR 0022):
   * - `boolean`: an icon, start-aligned like every column so it lines up
   *   with its filter (ADR 0025);
   * - `datetime`: a `<DateTime>` stacks as date over time from `desktop`;
   * - `number`: end-aligned tabular figures.
   * Text is the default.
   */
  kind?: 'boolean' | 'datetime' | 'number'
}

export interface DataTableProps<Row> {
  columns: readonly DataTableColumn<Row>[]
  rows: readonly Row[]
  getRowKey: (row: Row) => string | number
  /** Names the table for assistive technology (visually hidden caption). */
  caption: string
  sort?: DataTableSort | undefined
  onSortChange?: (sort: DataTableSort) => void
  /** Activates a row: a plain click on the row or its first-cell link. */
  onRowClick?: (row: Row) => void
  /** Makes the first cell a real link (keyboard, middle-click, open in new tab). */
  getRowHref?: (row: Row) => string
  /** Shows skeleton rows instead of `rows` and sets aria-busy. */
  isLoading?: boolean
  /** Skeleton row count while loading. Default 5. */
  loadingRows?: number
  /** Shown in a full-width row when not loading and there are no rows. */
  emptyState?: ReactNode
  density?: 'compact' | 'comfortable'
  /** Below this breakpoint each row renders as a stacked card. */
  stackBelow?: 'tablet'
  className?: string
}

const INTERACTIVE =
  'a, button, input, select, textarea, label, summary, [role="button"], [role="link"], [role="checkbox"], [tabindex]'

const ARIA_SORT = { ASC: 'ascending', DESC: 'descending' } as const

const sortFieldOf = <Row,>(column: DataTableColumn<Row>) => column.field ?? column.key
const isSortableColumn = <Row,>(column: DataTableColumn<Row>) =>
  column.isSortable ?? column.field !== undefined
const labelOf = <Row,>(column: DataTableColumn<Row>) =>
  column.label ?? (typeof column.header === 'string' ? column.header : column.key)

function valueOf<Row>(column: DataTableColumn<Row>, row: Row): ReactNode {
  if (column.render) return column.render(row)
  if (column.field === undefined) return null
  const value = row[column.field]
  if (value === null || value === undefined) return null
  return typeof value === 'object' ? JSON.stringify(value) : String(value)
}

/** Skeleton width per column, so loading rows look like text rather than bars. */
const SKELETON_WIDTHS = ['70%', '45%', '85%', '55%', '35%']

/**
 * A real `<table>` (SPEC §5.2, ADR 0012, 0022), always inside a TableFrame,
 * which owns its scroller (ADR 0025):
 * - below `desktop` rows stay one line;
 * - from `desktop` columns size to their content and cells wrap;
 * - the header is sticky against whichever box scrolls; the frame says when
 *   it is stuck, and which columns have a filter that needs room;
 * - `stackBelow="tablet"` turns each row into a labelled card on phones, on
 *   the same markup with explicit table roles.
 */
export function DataTable<Row>({
  columns,
  rows,
  getRowKey,
  caption,
  sort,
  onSortChange,
  onRowClick,
  getRowHref,
  isLoading = false,
  loadingRows = 5,
  emptyState,
  density = 'comfortable',
  stackBelow,
  className
}: DataTableProps<Row>) {
  const captionId = `data-table-${useId()}`
  const frame = useTableFrame()
  const filteredColumns = frame?.filteredColumns
  const isStacked = stackBelow !== undefined
  const hasRowAction = Boolean(onRowClick ?? getRowHref)
  // Explicit roles survive the display:block of the stacked layout (Safari drops implicit ones).
  const role = (name: string) => (isStacked ? { role: name } : {})

  // A filter spans from its column to the next filtered one, so only a
  // filtered column directly followed by another (or the last column) must
  // hold a whole filter itself; any other can stay as narrow as its data
  // (an icon) and its filter runs over the columns after it (ADR 0023).
  const needsFilterRoom = (key: string) => {
    if (!filteredColumns?.includes(key)) return false
    const index = columns.findIndex((column) => column.key === key)
    const next = columns[index + 1]
    return next === undefined || filteredColumns.includes(next.key)
  }

  const changeSort = (field: string) => {
    if (!onSortChange) return
    const order: SortOrder = sort?.field === field && sort.order === 'ASC' ? 'DESC' : 'ASC'
    onSortChange({ field, order })
  }

  const header = (column: DataTableColumn<Row>) => {
    const field = sortFieldOf(column)
    const isSorted = sort?.field === field
    const canSort = isSortableColumn(column) && onSortChange !== undefined
    const Icon = !isSorted
      ? MdOutlineUnfoldMore
      : sort.order === 'ASC'
        ? MdOutlineArrowUpward
        : MdOutlineArrowDownward
    return (
      <th
        key={column.key}
        scope="col"
        data-column-key={column.key}
        {...role('columnheader')}
        aria-sort={isSorted ? ARIA_SORT[sort.order] : undefined}
        className={clx(
          'data-table__header',
          (column.align === 'end' || column.kind === 'number') && 'data-table__header--is-end',
          needsFilterRoom(column.key) && 'data-table__header--is-filtered'
        )}
      >
        {canSort ? (
          <button
            type="button"
            className="data-table__sort"
            onClick={() => {
              changeSort(field)
            }}
          >
            <span className="data-table__sort-label">{column.header}</span>
            <span className="data-table__sort-icon" aria-hidden="true">
              <Icon />
            </span>
          </button>
        ) : (
          column.header
        )}
      </th>
    )
  }

  const content = (column: DataTableColumn<Row>, row: Row) => {
    const value = valueOf(column, row)
    if (value === null || value === '') return value
    // One-word values (ids, codes like C1-EWS) never break, truncated column
    // or not: the column grows to fit them. An e-mail address stays on one
    // line up to a cap, then ends in an ellipsis with the whole address in
    // the tooltip.
    if (typeof value === 'string' && !/\s/.test(value)) {
      const at = value.indexOf('@')
      if (at > 0 && at < value.length - 1) {
        return (
          <Tooltip content={value}>
            <span className="data-table__token data-table__token--as-email">{value}</span>
          </Tooltip>
        )
      }
      // A long hyphenated token (a GUID) may break at its hyphens.
      return (
        <span
          className={clx(
            'data-table__token',
            value.length > 24 && value.includes('-') && 'data-table__token--has-break'
          )}
        >
          {value}
        </span>
      )
    }
    if (!column.isTruncated) return value
    return (
      <Tooltip content={value}>
        <span className="data-table__truncate">{value}</span>
      </Tooltip>
    )
  }

  const primary = (row: Row, value: ReactNode) => {
    const href = getRowHref?.(row)
    if (href !== undefined) {
      return (
        <a
          href={href}
          className="data-table__link"
          onClick={(event) => {
            if (!onRowClick || event.defaultPrevented || event.button !== 0) return
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
            event.preventDefault()
            onRowClick(row)
          }}
        >
          {value}
        </a>
      )
    }
    if (onRowClick) {
      return (
        <button
          type="button"
          className="data-table__link"
          onClick={() => {
            onRowClick(row)
          }}
        >
          {value}
        </button>
      )
    }
    return value
  }

  // A click anywhere else on the row forwards to its first-cell link/button,
  // so modifier keys, routing and onRowClick all go through one element.
  const clickRow = (event: MouseEvent<HTMLTableRowElement>) => {
    const row = event.currentTarget
    const target = event.target as Element
    const interactive = target.closest(INTERACTIVE)
    if (interactive && row.contains(interactive)) return
    if (window.getSelection()?.toString()) return
    const link = row.querySelector<HTMLElement>('.data-table__link')
    link?.dispatchEvent(
      new window.MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        shiftKey: event.shiftKey,
        altKey: event.altKey
      })
    )
  }

  const cellProps = (column: DataTableColumn<Row>) => ({
    ...role('cell'),
    ...(isStacked && { 'data-label': labelOf(column) }),
    className: clx(
      'data-table__cell',
      (column.align === 'end' || column.kind === 'number') && 'data-table__cell--is-end',
      column.kind && `data-table__cell--as-${column.kind}`
    )
  })

  let body: ReactNode
  if (isLoading) {
    body = Array.from({ length: loadingRows }, (_, r) => (
      <tr key={`skeleton-${r}`} {...role('row')} className="data-table__row" aria-hidden="true">
        {columns.map((column, c) => (
          <td key={column.key} {...cellProps(column)}>
            <Skeleton width={SKELETON_WIDTHS[(r + c) % SKELETON_WIDTHS.length] ?? '60%'} />
          </td>
        ))}
      </tr>
    ))
  } else if (rows.length === 0) {
    body = emptyState != null && (
      <tr {...role('row')} className="data-table__row">
        <td {...role('cell')} colSpan={columns.length} className="data-table__empty">
          {emptyState}
        </td>
      </tr>
    )
  } else {
    body = rows.map((row) => (
      <tr
        key={getRowKey(row)}
        {...role('row')}
        className="data-table__row"
        {...(hasRowAction && { onClick: clickRow })}
      >
        {columns.map((column, c) => (
          <td key={column.key} {...cellProps(column)}>
            {c === 0 && hasRowAction ? primary(row, content(column, row)) : content(column, row)}
          </td>
        ))}
      </tr>
    ))
  }

  return (
    <div
      className={clx(
        'data-table',
        density === 'compact' && 'data-table--is-compact',
        isStacked && 'data-table--has-stack',
        hasRowAction && 'data-table--has-row-action',
        className
      )}
      data-stuck={frame?.isStuck || undefined}
    >
      <table
        {...role('table')}
        className="data-table__table"
        aria-labelledby={captionId}
        aria-busy={isLoading || undefined}
      >
        <caption id={captionId} className="data-table__caption">
          <VisuallyHidden>{caption}</VisuallyHidden>
        </caption>
        {columns.some((column) => column.width) && (
          <colgroup>
            {columns.map((column) => (
              <col key={column.key} style={column.width ? { width: column.width } : undefined} />
            ))}
          </colgroup>
        )}
        <thead {...role('rowgroup')} className="data-table__head">
          <tr {...role('row')} className="data-table__row">
            {columns.map(header)}
          </tr>
        </thead>
        <tbody {...role('rowgroup')} className="data-table__body">
          {body}
        </tbody>
      </table>
    </div>
  )
}
